import { useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { createPayStub, deletePayStub, listPayStubs } from "@/features/worksheets/api";
import type { Employer, PayStub } from "@/features/worksheets/types";

interface Props {
  clientId: string;
  employer: Employer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Manages the pay-stub line items for a single employer (add / list / delete).
 * Amounts are decimal strings on the clients API. */
export function PayStubsDialog({ clientId, employer, open, onOpenChange }: Props) {
  const [rows, setRows] = useState<PayStub[]>([]);
  const [item, setItem] = useState("");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !employer) return;
    let active = true;
    listPayStubs<PayStub>(clientId, employer.id)
      .then((r) => active && setRows(r))
      .catch(() => active && setRows([]));
    return () => {
      active = false;
    };
  }, [open, employer, clientId]);

  async function reload() {
    if (!employer) return;
    setRows(await listPayStubs<PayStub>(clientId, employer.id));
  }

  async function add() {
    if (!employer || !item.trim()) return;
    setBusy(true);
    try {
      await createPayStub(clientId, employer.id, {
        item: item.trim(),
        category: category.trim(),
        amount: amount || "0",
      });
      setItem("");
      setCategory("");
      setAmount("");
      await reload();
    } catch {
      toast.error("Couldn't add pay-stub line.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!employer) return;
    try {
      await deletePayStub(clientId, employer.id, id);
      await reload();
    } catch {
      toast.error("Couldn't delete line.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Pay stubs — {employer?.name}</DialogTitle>
        </DialogHeader>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground py-4 text-center">
                  No pay-stub lines yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.item}</TableCell>
                  <TableCell>{r.category}</TableCell>
                  <TableCell className="text-right">${r.amount}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label="Delete"
                      onClick={() => remove(r.id)}
                    >
                      <FiTrash2 className="text-destructive size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div className="grid grid-cols-[1fr_1fr_auto_auto] items-end gap-2">
          <div className="grid gap-1">
            <Label htmlFor="ps-item">Item</Label>
            <Input id="ps-item" value={item} onChange={(e) => setItem(e.target.value)} placeholder="Gross pay" />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="ps-cat">Category</Label>
            <Input id="ps-cat" value={category} onChange={(e) => setCategory(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="ps-amt">Amount</Label>
            <Input
              id="ps-amt"
              value={amount}
              inputMode="decimal"
              placeholder="0.00"
              // Dollars with up to 2 decimals only — mirrors the backend
              // DecimalField(12,2) so letters/≥3-decimals/negatives can't be typed.
              onChange={(e) => {
                const raw = e.target.value;
                // ≤10 integer digits + ≤2 decimals → matches DecimalField(12,2).
                if (raw === "" || /^\d{0,10}\.?\d{0,2}$/.test(raw)) setAmount(raw);
              }}
              className="w-28"
            />
          </div>
          <Button
            type="button"
            onClick={add}
            disabled={busy || !item.trim() || (amount !== "" && !/^\d{1,10}(\.\d{1,2})?$/.test(amount))}
          >
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
